import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import type { AuditHistoryEntry } from '../../../../../infrastructure/OMSClient';
import { formatUnixTimestampToUTC, formatUnixTimestampToLocal } from '../../../../../utils/dateUtils';
import { kstTimestamp } from '../../../../../utils/fulfillmentTime';

interface AuditEntryProps {
  entry: AuditHistoryEntry;
  /** Warehouse screens read the trail on the Seoul clock; the legacy OMS audit page keeps the reader's own. */
  kst?: boolean;
}

/** The trail stamps whole seconds, but a 13-digit value is milliseconds — the same rule the other formatters apply. */
const auditInstant = (unixTimestamp: number): string =>
  new Date(unixTimestamp.toString().length <= 10 ? unixTimestamp * 1000 : unixTimestamp).toISOString();

/** The envelope the writer stamps on every payload; it says nothing about what changed. */
const AUDIT_ENVELOPE = 'auditEntry';

/**
 * A nested value is summarised by its size rather than flattened: the reader is scanning for which
 * fields a write touched, and a whole shipment inlined here buries the next entry.
 */
const describeValue = (value: unknown): string => {
  // An empty string is a value the write cleared, and blank space does not read as one.
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (typeof value === 'object') {
    const count = Object.keys(value as Record<string, unknown>).length;
    return `${count} field${count === 1 ? '' : 's'}`;
  }
  return String(value);
};

/** One row of the trail: what was done, by whom, when, and which fields it touched. */
export function AuditEntry({ entry, kst = false }: AuditEntryProps) {
  const changes = Object.entries(entry.updatedData ?? {}).filter(([key]) => key !== AUDIT_ENVELOPE);

  return (
    <Card className="mb-3">
      <CardHeader className="py-2">
        <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
          {/* Bootstrap sizing, not Tailwind: this build prefixes Tailwind with `tw-`, so `text-sm` emits nothing and the h3 stays 24px. */}
          <CardTitle className="fs-6 fw-medium mb-0">
            {/* The operation is the sentence; the source and operator are who spoke it. */}
            <span className="font-monospace fw-semibold">{entry.operation?.toUpperCase()}</span>
            <span className="ms-2 text-muted small">{entry.source}</span>
            {entry.operator && <code className="ms-1 small text-primary">{entry.operator}</code>}
          </CardTitle>
          <div className="text-muted small text-end" data-testid="audit-time">
            <div>
              {kst
                ? kstTimestamp(auditInstant(entry.updatedTimestamp))
                : formatUnixTimestampToLocal(entry.updatedTimestamp)}
            </div>
            {/* The UTC formatter names its own zone — adding another label here would read as "09:20:06 UTC UTC". */}
            <div>{formatUnixTimestampToUTC(entry.updatedTimestamp)}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="py-2">
        {changes.length === 0 ? (
          <div className="text-muted small fst-italic">No data changes — audit entry only</div>
        ) : (
          <>
            <dl className="mb-2" data-testid="audit-changes">
              {changes.map(([key, value]) => (
                <div className="d-flex justify-content-between gap-3" key={key}>
                  <dt className="small text-muted fw-normal">{key}</dt>
                  <dd className="small fw-medium mb-1 text-end text-break">{describeValue(value)}</dd>
                </div>
              ))}
            </dl>
            {/* Same disclosure the order facts use, so nothing the summary abbreviates becomes unreachable. */}
            <details>
              <summary className="small text-muted">Raw</summary>
              <pre className="bg-light border rounded mb-0 mt-1 p-2 small text-break">
                {JSON.stringify(Object.fromEntries(changes), null, 2)}
              </pre>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
}
